import { CargoCoolingRange, CargoType, CargoTypeSub, ServiceType } from '@prisma/client';

import { AirCanada } from '../../../api/src/lib/integrations/ac/typings';
import { Emirates } from '../../../api/src/lib/integrations/ek/typings';
import { IATA } from '../../../api/src/lib/integrations/iata';
import { Qatar } from '../../../api/src/lib/integrations/qr/typings';
import { Turkish } from '../../../api/src/lib/integrations/tk/typings';
import { COMMODITIES_GENERATED as COMMODITIES } from './commodities.generated';

export type AirlineProductToPeli<T> = {
  name: string;
  code: T;
  shcs?: string[];
  cargoType: CargoType;
  cargoTypeSub?: CargoTypeSub | null;
  serviceType: ServiceType;

  visibleTo?: {
    cargoTypes: (CargoType | null | undefined)[];
    cargoTypeSubs?: (CargoTypeSub | null | undefined)[];
    serviceTypes?: ServiceType[];
    cargoCoolingRanges?: CargoCoolingRange[];
    unNumbers?: string[];
  };
};

export type AirlineProductToPeliMap<T extends string> = Record<T, AirlineProductToPeli<T>>;

export const EK_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Emirates.ProductCode> = {
  [Emirates.ProductCode.GCR]: {
    name: 'Emirates AirFreight',
    code: Emirates.ProductCode.GCR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [null, undefined, CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable],
      cargoTypeSubs: [null, undefined, CargoTypeSub.GENERAL],
    },
  },
  [Emirates.ProductCode.AXA]: {
    name: 'Emirates AirFreight Priority',
    code: Emirates.ProductCode.AXA,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,

    visibleTo: {
      cargoTypes: [null, undefined, CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable],
      cargoTypeSubs: [null, undefined, CargoTypeSub.GENERAL],
    },
  },
  [Emirates.ProductCode.PXS]: {
    name: 'Emirates Fresh',
    code: Emirates.ProductCode.PXS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.PXD]: {
    name: 'Emirates Fresh Breathe',
    code: Emirates.ProductCode.PXD,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.PNF]: {
    name: 'Emirates Fresh Perishable Non Food',
    code: Emirates.ProductCode.PNF,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.GENERAL,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.PXF]: {
    name: 'Emirates Fresh Perishable Frozen',
    code: Emirates.ProductCode.PXF,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,
  },
  [Emirates.ProductCode.AXM]: {
    name: 'Emirates Medical Devices',
    code: Emirates.ProductCode.AXM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.AXS]: {
    name: 'Emirates Pharma',
    code: Emirates.ProductCode.AXS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.AXD]: {
    name: 'Emirates Pharma Plus',
    code: Emirates.ProductCode.AXD,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.Expedited,
  },
  [Emirates.ProductCode.AXT]: {
    name: 'Emirates Vital',
    code: Emirates.ProductCode.AXT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.VAL]: {
    name: 'Emirates Secure VAL',
    code: Emirates.ProductCode.VAL,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.VUN]: {
    name: 'Emirates Secure VUN',
    code: Emirates.ProductCode.VUN,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.AOG]: {
    name: 'Emirates AOG',
    code: Emirates.ProductCode.AOG,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
  [Emirates.ProductCode.PAX]: {
    name: 'Emirates DG-PAX',
    code: Emirates.ProductCode.PAX,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.CAO]: {
    name: 'Emirates DG-CAO',
    code: Emirates.ProductCode.CAO,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
};

export const AC_PRODUCT_CODE_PELI: AirlineProductToPeliMap<AirCanada.ProductCode> = {
  [AirCanada.ProductCode.AFR]: {
    name: 'AC General - Standard',
    code: AirCanada.ProductCode.AFR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PPR]: {
    name: 'AC General - Priority1',
    code: AirCanada.ProductCode.PPR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.ADG]: {
    name: 'AC DGR - Standard',
    code: AirCanada.ProductCode.ADG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PDG]: {
    name: 'AC DGR - Priority1',
    code: AirCanada.ProductCode.PDG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.PPS]: {
    name: 'AC Pharmacair Simple',
    code: AirCanada.ProductCode.PPS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    // cargoTypeSub: CargoTypeSub.GENERAL, // ALSO
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PPP]: {
    name: 'AC Pharmacair Plus',
    code: AirCanada.ProductCode.PPP,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    // cargoTypeSub: CargoTypeSub.GENERAL, // ALSO
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.AFH]: {
    name: 'AC Fresh - Standard',
    code: AirCanada.ProductCode.AFH,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PFH]: {
    name: 'AC Fresh - Priority',
    code: AirCanada.ProductCode.PFH,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,
  },
};

export const QR_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Qatar.ProductCode> = {
  [Qatar.ProductCode.GCR]: {
    name: 'GCR',
    code: Qatar.ProductCode.GCR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.XPS]: {
    name: 'Q-Plus',
    code: Qatar.ProductCode.GCR,
    shcs: [IATA.HandlingCode.XPS],
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [Qatar.ProductCode.VIC]: {
    name: 'Q-Prime',
    code: Qatar.ProductCode.GCR,
    shcs: [IATA.HandlingCode.VIC],
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
  [Qatar.ProductCode.QRDG]: {
    name: 'QRDG',
    code: Qatar.ProductCode.QRDG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.PHARM]: {
    name: 'PHARM',
    code: Qatar.ProductCode.PHARM,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.SLIFT]: {
    name: 'SLIFT',
    code: Qatar.ProductCode.SLIFT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.QFR]: {
    name: 'QFR',
    code: Qatar.ProductCode.QFR,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,
  },
  [Qatar.ProductCode.SEM]: {
    name: 'SEM',
    code: Qatar.ProductCode.SEM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
};

/**
 * TK returns one rate per product on every flight combination. The applicable rate is TK's
 * non-express ("TK Smart") rate; the other two are their express products.
 */
export const TK_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Turkish.ProductCode> = {
  [Turkish.ProductCode.SMART]: {
    name: 'TK Smart',
    code: Turkish.ProductCode.SMART,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Turkish.ProductCode.PREMIUM]: {
    name: 'TK Premium',
    code: Turkish.ProductCode.PREMIUM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [Turkish.ProductCode.URGENT]: {
    name: 'TK Urgent',
    code: Turkish.ProductCode.URGENT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
};

export type CommodityAirline = 'AC' | 'EK' | 'QR';

export type CommodityMapAirline = {
  airline: CommodityAirline;

  commodityCode: string;
  product: string;

  productSHC?: string;
  commoditySHC?: string;
};

export type CommodityItem = {
  value: string;
  label: string;
  isGeneric?: boolean;
  sortPosition?: number;

  airline?: CommodityMapAirline[];
};

export const getPrettyCommodity = (value: string) => {
  // `||` not `??`: a commodity with a blank label must still render as something.
  return findCommodity(value)?.label || value;
};

const normalizeCommodityKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const findCommodity = (value: string) => {
  if (!value) return undefined;

  const exact = COMMODITIES?.find((n) => n.value === value);
  if (exact) return exact;

  const key = normalizeCommodityKey(value);
  if (!key) return undefined;
  return COMMODITIES?.find((n) => normalizeCommodityKey(n.value) === key);
};

export const getCommoditiesFor = (
  cargoType: CargoType,
  { excludeGeneric, cargoTypeSub }: { excludeGeneric?: boolean; cargoTypeSub?: CargoTypeSub } = {
    excludeGeneric: false,
  },
) => {
  // We are using only EK as the base right now since they are the most restrictive.
  const products = Object.values(EK_PRODUCT_CODE_PELI)
    ?.filter((node) => {
      const matchesCargoType = node?.visibleTo?.cargoTypes?.includes(cargoType);
      const matchesCargoTypeSub = cargoTypeSub ? node?.visibleTo?.cargoTypeSubs?.includes(cargoTypeSub) : true;

      return matchesCargoType && matchesCargoTypeSub;
    })
    ?.map((node) => node.code as Emirates.ProductCode);

  if (!products.length) return COMMODITIES;

  const filtered = COMMODITIES?.filter((node) => {
    if (excludeGeneric && node?.isGeneric) return false;
    return node.airline?.some((item) => products.includes(item.product as Emirates.ProductCode));
  })
    // A blank label renders as an invisible row in the picker — fall back to the
    // canonical value so the option is always selectable.
    .map((node) => (node.label ? node : { ...node, label: node.value }));

  return filtered?.sort((a, b) => {
    const aHasSort = a.sortPosition !== undefined;
    const bHasSort = b.sortPosition !== undefined;

    // Both have sort position, sort by it
    if (aHasSort && bHasSort) return a.sortPosition! - b.sortPosition!;

    // One has sort position, put it first
    if (aHasSort) return -1;
    if (bHasSort) return 1;

    // Neither has sort position, sort by label
    return a.label.localeCompare(b.label);
  });
};

export { COMMODITIES };
